import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { subscriptionApi, companyApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Check, CreditCard, Loader2, Sparkles } from "lucide-react";

const SubscriptionPage = () => {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [plansRes, companyRes] = await Promise.all([
        subscriptionApi.getPlans(),
        companyApi.getMyCompany().catch(() => null)
      ]);
      setPlans(plansRes.data);
      if (companyRes) setCompany(companyRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.info("No pudimos verificar el pago. Por favor revisa tu correo para confirmación.");
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await subscriptionApi.getStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        toast.success("¡Pago exitoso! Tu suscripción ha sido activada.");
        setCheckingPayment(false);
        loadData();
        // Clear session_id from URL
        window.history.replaceState({}, '', '/dashboard/subscription');
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setCheckingPayment(false);
      toast.error("Error al verificar el pago");
    }
  };

  const handleSubscribe = async (planId) => {
    if (!company) {
      toast.error("Primero debes configurar tu empresa");
      return;
    }

    setProcessingPlan(planId);

    try {
      const response = await subscriptionApi.createCheckout({
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar el pago");
      setProcessingPlan(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Suscripción</h1>
        <p className="text-slate-600">Elige el plan que mejor se adapte a tu negocio</p>
      </div>

      {/* Payment checking overlay */}
      {checkingPayment && (
        <Card className="cia-card border-[#5B9BD5] bg-blue-50">
          <CardContent className="p-6 flex items-center gap-4">
            <Loader2 className="w-6 h-6 animate-spin text-[#5B9BD5]" />
            <div>
              <p className="font-medium text-slate-900">Verificando pago...</p>
              <p className="text-sm text-slate-600">Por favor espera mientras confirmamos tu pago.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current subscription status */}
      {company && (
        <Card className={`cia-card ${company.subscription_status === 'active' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`font-semibold ${company.subscription_status === 'active' ? 'text-green-800' : 'text-amber-800'}`}>
                  {company.subscription_status === 'active' ? 'Suscripción Activa' : 'Periodo de Prueba'}
                </h3>
                <p className={`text-sm ${company.subscription_status === 'active' ? 'text-green-700' : 'text-amber-700'}`}>
                  {company.subscription_status === 'active' 
                    ? `Plan: ${company.subscription_plan || 'N/A'}`
                    : 'Actualiza tu plan para acceder a todas las funciones'}
                </p>
              </div>
              {company.subscription_status === 'active' && (
                <span className="badge-paid">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Activa
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = company?.subscription_plan === plan.id && company?.subscription_status === 'active';
          const isPopular = plan.id === 'professional';
          
          return (
            <Card 
              key={plan.id}
              className={`cia-card relative ${isPopular ? 'border-[#5B9BD5] border-2 shadow-lg' : ''}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#5B9BD5] text-white text-xs font-bold px-4 py-1 rounded-full">
                  Más Popular
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-black text-slate-900">{formatCurrency(plan.price)}</span>
                  <span className="text-slate-500">/mes</span>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-600">
                      <Check className="w-5 h-5 text-[#5B9BD5] flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan || processingPlan === plan.id || !company}
                  className={`w-full py-6 ${isPopular ? 'cia-btn-primary' : 'cia-btn-secondary'}`}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {processingPlan === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isCurrentPlan ? (
                    'Plan Actual'
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Seleccionar Plan
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No company warning */}
      {!company && (
        <Card className="cia-card border-amber-200 bg-amber-50">
          <CardContent className="p-6 text-center">
            <p className="text-amber-800">
              Necesitas <a href="/dashboard/settings" className="font-medium underline">configurar tu empresa</a> antes de suscribirte a un plan.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Payment info */}
      <Card className="cia-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Pago Seguro</h3>
              <p className="text-sm text-slate-600 mt-1">
                Procesamos pagos de forma segura a través de Stripe. Aceptamos tarjetas de crédito y débito.
                Puedes cancelar tu suscripción en cualquier momento.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionPage;
